import { useEffect, useState } from 'react';
import { useWallet, type PossessionId } from '@/state/wallet';
import { useMarkets } from '@/state/markets';
import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useNotifications } from '@/state/notifications';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useMeta } from '@/state/meta';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { money, priceFmt, pct } from '@/lib/format';
import { sounds } from '@/audio/synth';
import { cn } from '@/lib/cn';

export function Wallet() {
  const cash = useMarkets((s) => s.cash);
  const realizedPnl = useMarkets((s) => s.realizedPnl);
  const positions = useMarkets((s) => s.positions);
  const setActiveTicker = useMarkets((s) => s.setActiveTicker);
  const closePosition = useMarkets((s) => s.applyFill);
  const possessions = useWallet((s) => s.possessions);
  const sell = useWallet((s) => s.sellPossession);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleSell = (id: PossessionId) => {
    const p = sell(id);
    if (!p) return;
    useMarkets.getState().addCash(p.sellPrice);
    sounds.fill();
    routeSellConsequences(id, p.sellPrice);
  };

  // Live total: cash + market value of positions, computed inline so it updates
  // as prices wander (no extra subscriptions).
  let posVal = 0;
  for (const p of positions) {
    const cfg = getTicker(p.ticker);
    if (cfg) posVal += p.qty * priceAt(cfg, now);
  }
  const netWorth = cash + posVal;

  return (
    <div className="flex h-full flex-col bg-[var(--bg-deep)]">
      <header className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 py-3">
        <span className="text-mono-md font-bold text-[var(--text-primary)]">Wallet</span>
        <span className="text-mono-xs text-[var(--text-muted)]">
          NW <span className="text-[var(--text-primary)]">{money(netWorth)}</span> · {money(cash)} liquid
        </span>
      </header>
      <div className="flex-1 overflow-y-auto p-4">
        <Section title="Cash & balances">
          <Row label="Net worth" value={money(netWorth)} />
          <Row label="Cash" value={money(cash)} />
          <Row label="Open Positions" value={`${positions.length}`} />
          <Row label="Realized P&L" value={money(realizedPnl)} positive={realizedPnl >= 0} />
        </Section>

        {positions.length > 0 && (() => {
          // Pre-compute market values so we can render concentration bars relative to total exposure.
          const enriched = positions.map((p) => {
            const cfg = getTicker(p.ticker);
            const live = cfg ? priceAt(cfg, now) : 0;
            const value = live * p.qty;
            return { p, cfg, live, value };
          });
          const exposure = enriched.reduce((acc, x) => acc + x.value, 0);
          const nw = cash + exposure;
          const liquidateAll = () => {
            if (!confirm(`Sell ALL ${enriched.length} positions at market? This is final.`)) return;
            for (const { p, cfg, live } of enriched) {
              if (cfg) closePosition(p.ticker, 'sell', p.qty, live);
            }
            sounds.fill();
          };
          return (
            <Section title={`Positions · ${money(exposure)} exposure (${nw > 0 ? ((exposure / nw) * 100).toFixed(0) : 0}% of NW)`}>
              {enriched.length > 1 && (
                <button
                  onClick={liquidateAll}
                  className="self-end rounded border border-[var(--negative)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--negative)] hover:bg-[var(--negative)] hover:text-black"
                  title="Sell every position at market — emergency liquidation"
                >
                  panic sell all
                </button>
              )}
              {enriched.map(({ p, cfg, live, value }) => {
                if (!cfg) return null;
                const upnl = (live - p.avgPrice) * p.qty;
                const upnlPct = live / p.avgPrice - 1;
                const positive = upnl >= 0;
                const concentration = exposure > 0 ? value / exposure : 0;
                return (
                  <div
                    key={p.ticker}
                    className="flex flex-col gap-1.5 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => setActiveTicker(p.ticker)}
                        className="flex flex-col items-start text-left flex-1 min-w-0"
                        title="open chart"
                      >
                        <span className="text-mono-sm font-bold text-[var(--text-primary)]">{p.ticker}</span>
                        <span className="text-[10px] text-[var(--text-muted)]">{p.qty.toFixed(0)} @ {priceFmt(p.avgPrice)} → {priceFmt(live)}</span>
                      </button>
                      <div className="flex flex-col items-end shrink-0">
                        <span className={cn('text-mono-sm tabular-nums', positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>{money(upnl)}</span>
                        <span className={cn('text-[10px] tabular-nums', positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>{pct(upnlPct)}</span>
                      </div>
                      <button
                        onClick={() => { closePosition(p.ticker, 'sell', p.qty, live); sounds.fill(); }}
                        className="rounded border border-[var(--border-subtle)] px-2 py-0.5 text-[10px] uppercase text-[var(--text-secondary)] hover:bg-[var(--negative)] hover:text-black shrink-0"
                        title="close at market"
                      >
                        close
                      </button>
                    </div>
                    <Sparkline cfg={cfg} now={now} positive={positive} />
                    <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
                      <div className="h-1 flex-1 rounded bg-[var(--bg-elevated)]">
                        <div
                          className="h-full rounded bg-[var(--accent-primary)] transition-all"
                          style={{ width: `${(concentration * 100).toFixed(1)}%` }}
                        />
                      </div>
                      <span className="tabular-nums shrink-0">{(concentration * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })}
            </Section>
          );
        })()}

        <Section title="Possessions">
          {possessions.map((p) => (
            <div
              key={p.id}
              className={cn(
                'flex flex-col gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3',
                !p.available && 'opacity-40',
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-mono-sm font-bold text-[var(--text-primary)]">{p.label}</span>
                <span className="text-mono-xs tabular-nums text-[var(--text-muted)]">
                  {money(p.sellPrice)}
                </span>
              </div>
              <p className="text-mono-xs text-[var(--text-secondary)]">{p.description}</p>
              <button
                onClick={() => handleSell(p.id)}
                disabled={!p.available}
                className={cn(
                  'self-start rounded px-3 py-1 text-mono-xs font-bold transition-colors',
                  p.available
                    ? 'bg-[var(--negative)] text-black hover:brightness-110'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] cursor-not-allowed',
                )}
              >
                {p.available ? 'sell' : 'sold'}
              </button>
            </div>
          ))}
        </Section>

        <Section title="Acquisitions">
          <AcquireRow
            label="Charity Foundation"
            description="Tax shelter. -50% IRS heat over time."
            cost={500_000}
            owned={useWallet((s) => s.ownsCharityFoundation)}
            available={useMeta((s) => s.highestTierRankReached) >= 3}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 500_000) return;
              useMarkets.getState().addCash(-500_000);
              useWallet.getState().acquireFoundation();
              useLife.getState().bumpIrsHeat(-25);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'charity foundation acquired. \"art\" deductions enabled.',
              });
              sounds.fill();
            }}
          />
          <AcquireRow
            label="Private Jet"
            description="Status flex. +10 notoriety."
            cost={5_000_000}
            owned={useWallet((s) => s.ownsJet)}
            available={useMeta((s) => s.highestTierRankReached) >= 4}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 5_000_000) return;
              useMarkets.getState().addCash(-5_000_000);
              useWallet.getState().acquireJet();
              usePower.getState().bumpNotoriety(10);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'private jet acquired. greta is, somewhere, mad.',
              });
              sounds.fill();
            }}
          />
          <AcquireRow
            label="Yacht"
            description="240 feet of nothing-to-do. +5 marriage rep."
            cost={20_000_000}
            owned={useWallet((s) => s.ownsYacht)}
            available={useMeta((s) => s.highestTierRankReached) >= 4}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 20_000_000) return;
              useMarkets.getState().addCash(-20_000_000);
              useWallet.getState().acquireYacht();
              useLife.getState().bumpRelationship('wife', 5);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'yacht acquired. wife will use it more than you will.',
              });
              sounds.fill();
            }}
          />
          <AcquireRow
            label="Private Island"
            description="A small island in the Caribbean. Avoids 4 jurisdictions. +20 notoriety."
            cost={150_000_000}
            owned={useWallet((s) => s.ownsIsland)}
            available={useMeta((s) => s.highestTierRankReached) >= 4}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 150_000_000) return;
              useMarkets.getState().addCash(-150_000_000);
              useWallet.getState().acquireIsland();
              useLife.getState().bumpFbiHeat(5);
              usePower.getState().bumpNotoriety(20);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'private island acquired. the FBI noticed.',
              });
              sounds.fill();
            }}
          />
          <AcquireRow
            label="NBA Franchise"
            description="A sports team. Status flex. +30 notoriety. Court TBD on whether you actually run it."
            cost={3_500_000_000}
            owned={useWallet((s) => s.ownsSportsTeam)}
            available={useMeta((s) => s.highestTierRankReached) >= 4}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 3_500_000_000) return;
              useMarkets.getState().addCash(-3_500_000_000);
              useWallet.getState().acquireSportsTeam();
              usePower.getState().bumpNotoriety(30);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'NBA franchise acquired. you have 14 calls scheduled with the commissioner this week.',
              });
              sounds.fill();
            }}
          />
          <AcquireRow
            label="Major Newspaper"
            description="A national newspaper. Press rep +10. Editor still hates you."
            cost={250_000_000}
            owned={useWallet((s) => s.ownsNewspaper)}
            available={useMeta((s) => s.highestTierRankReached) >= 4}
            onBuy={() => {
              const cash = useMarkets.getState().cash;
              if (cash < 250_000_000) return;
              useMarkets.getState().addCash(-250_000_000);
              useWallet.getState().acquireNewspaper();
              usePower.getState().bumpRep('press', 10);
              useNotifications.getState().push({
                surfaceId: 'wallet', severity: 'important',
                text: 'newspaper acquired. the editor has resigned in protest. the next editor will resign in protest. the cycle continues.',
              });
              sounds.fill();
            }}
          />
        </Section>

        <p className="mt-4 text-[10px] italic text-[var(--text-muted)]">
          desperation moves unlock as your situation gets worse. there is always one more move.
        </p>
      </div>
    </div>
  );
}

function AcquireRow({ label, description, cost, owned, available, onBuy }: { label: string; description: string; cost: number; owned: boolean; available: boolean; onBuy: () => void }) {
  return (
    <div className={cn(
      'flex flex-col gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3',
      !available && 'opacity-40',
    )}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-mono-sm font-bold text-[var(--text-primary)]">{label}</span>
        <span className="text-mono-xs tabular-nums text-[var(--text-muted)]">{money(cost)}</span>
      </div>
      <p className="text-mono-xs text-[var(--text-secondary)]">{description}</p>
      <button
        onClick={onBuy}
        disabled={owned || !available}
        className={cn(
          'self-start rounded px-3 py-1 text-mono-xs font-bold transition-colors',
          owned
            ? 'bg-[var(--positive)] text-black'
            : available
            ? 'bg-[var(--accent-primary)] text-black hover:brightness-110'
            : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] cursor-not-allowed',
        )}
      >
        {owned ? '✓ owned' : available ? `acquire (${money(cost)})` : 'tier-locked'}
      </button>
    </div>
  );
}

function Sparkline({ cfg, now, positive }: { cfg: NonNullable<ReturnType<typeof getTicker>>; now: number; positive: boolean }) {
  // 1h sparkline at 30 samples — cheap, deterministic from priceAt().
  const N = 30;
  const winMs = 60 * 60_000;
  const samples: number[] = [];
  for (let i = 0; i < N; i++) {
    samples.push(priceAt(cfg, now - winMs + (i * winMs) / (N - 1)));
  }
  const min = Math.min(...samples);
  const max = Math.max(...samples);
  const span = max - min || 1;
  const W = 100, H = 20;
  const points = samples.map((v, i) => {
    const x = (i / (N - 1)) * W;
    const y = H - ((v - min) / span) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const color = positive ? 'var(--positive)' : 'var(--negative)';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-5 w-full opacity-80">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <div className="mb-2 text-mono-xs font-bold uppercase tracking-wider text-[var(--accent-primary)]">{title}</div>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function Row({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-1.5 text-mono-sm">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className={cn(
        'tabular-nums',
        positive === true && 'text-[var(--positive)]',
        positive === false && 'text-[var(--negative)]',
        positive === undefined && 'text-[var(--text-primary)]',
      )}>
        {value}
      </span>
    </div>
  );
}

function routeSellConsequences(id: PossessionId, price: number): void {
  const life = useLife.getState();
  const xc = useXClone.getState();
  const phone = usePhone.getState();
  const notifs = useNotifications.getState();
  switch (id) {
    case 'dog':
      life.bumpSanity(-30);
      life.bumpRelationship('wife', -40);
      life.bumpRelationship('mom', -25);
      xc.receivePost({
        authorHandle: 'ReutersFlash',
        authorTribe: 'press',
        text: 'BREAKING: trader sells own dog to cover margin call. dog reportedly "fine" and "found a better home." source: trader, defensively.',
        likes: 4200,
        reposts: 980,
      });
      phone.receiveMessage({
        threadId: 'wife', fromNpc: 'wife',
        text: 'WHERE IS BARTHOLOMEW',
      });
      notifs.push({ surfaceId: 'phone', severity: 'urgent', text: 'wife: WHERE IS BARTHOLOMEW' });
      break;
    case 'watch':
      life.bumpRelationship('mom', -10);
      phone.receiveMessage({
        threadId: 'mom', fromNpc: 'mom',
        text: 'sweetie. dad noticed.',
      });
      notifs.push({ surfaceId: 'phone', severity: 'info', text: 'mom: dad noticed.' });
      break;
    case 'car':
      life.bumpSanity(-10);
      life.bumpRelationship('wife', -15);
      break;
    case 'house':
      life.bumpSanity(-20);
      life.bumpRelationship('wife', -50);
      life.setApartmentTier('studio');
      xc.receivePost({
        authorHandle: 'ShitcoinPaul',
        authorTribe: 'crypto',
        text: 'guy just sold his house to cover margin. respect. you are gonna make it.',
        likes: 3100,
        reposts: 410,
      });
      break;
  }
  notifs.push({ surfaceId: 'wallet', severity: 'info', text: `+${money(price)} from sale` });
}
